import type { TFunction } from 'i18next';

import {
  farmatodo_logo,
  heb_logo,
  kehe_logo,
  lowes_logo,
  meijer_logo,
  soriana_logo,
  target_forecast_logo,
  target_logo,
  unfi_logo,
  wrl_logo,
  wscintilla_logo,
} from '@Assets';
import { ConnectorConfig, ConnectorInstructions, ConnectorMetadataDto } from '@Models';

const CONNECTOR_GUIDE_PATHS: Record<string, string> = {
  Walmart: '/guides/wrl',
};

const resolveInstructions = (t: TFunction, connectorKey: string): ConnectorInstructions | undefined => {
  const guidePath = CONNECTOR_GUIDE_PATHS[connectorKey];
  if (guidePath) return { kind: 'link', url: guidePath };

  const stepsPath = `Credentials.Instructions.${connectorKey}.Steps`;
  const steps = t(stepsPath, { returnObjects: true });
  if (!Array.isArray(steps)) return undefined;

  const notePath = `Credentials.Instructions.${connectorKey}.Note`;
  const note = t(notePath);
  return {
    kind: 'steps',
    title: t(`Credentials.Instructions.${connectorKey}.Title`),
    steps: steps as string[],
    note: note === notePath ? undefined : note,
  };
};

const LOGO_ASSETS: Record<string, string> = {
  wrl: wrl_logo,
  farmatodo: farmatodo_logo,
  kehe: kehe_logo,
  target: target_logo,
  target_forecast: target_forecast_logo,
  unfi: unfi_logo,
  wscintilla: wscintilla_logo,
  soriana: soriana_logo,
  lowes: lowes_logo,
  meijer: meijer_logo,
  heb: heb_logo,
};

const CONNECTOR_CLASS_OVERRIDES: Partial<Record<string, Pick<ConnectorConfig, 'className'>>> = {
  wscintilla: { className: 'w-[20px]' },
};

const CONNECTOR_ALIASES: Record<string, string> = {
  'casa ley': 'casa_ley',
  'grupo control': 'grupo_control',
  'grupo la comer': 'lacomer',
  'grupo merza': 'merza',
};

export const resolveConnectorKey = (key: string): string => {
  const normalized = key.toLowerCase();
  return CONNECTOR_ALIASES[normalized] ?? normalized.replace(/\s+/g, '');
};

const adaptConnectorsFromApi = (connectors: ConnectorMetadataDto[], t: TFunction): Record<string, ConnectorConfig> => {
  return Object.fromEntries(
    connectors.map((meta) => [
      resolveConnectorKey(meta.connectorType),
      {
        icon: meta.logoKey ? LOGO_ASSETS[meta.logoKey] : undefined,
        name: meta.name,
        platform: meta.platform ?? undefined,
        avatarInitials: meta.avatarInitials ?? undefined,
        avatarColor: meta.avatarColor ?? undefined,
        readOnly: meta.readOnly,
        fields: meta.fields.map((f) => ({
          storageKey: f.storageKey,
          labelKey: f.labelKey,
          placeholderKey: f.placeholderKey ?? undefined,
          type: f.fieldType,
          isSecret: f.isSecret,
          isVirtualField: f.isVirtualField || undefined,
        })),
        instructions: meta.instructionsKey ? resolveInstructions(t, meta.instructionsKey) : undefined,
        ...CONNECTOR_CLASS_OVERRIDES[meta.connectorType],
      },
    ]),
  );
};

export { adaptConnectorsFromApi };
